import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RentalService } from '../../../core/api/rental.service';
import { PaymentService } from '../../../core/api/payment.service';
import { Rental, RentalExtraService } from '../../../core/models/rental.model';
import { Payment } from '../../../core/models/payment.model';
import { formatDateTime } from '../../../shared/utils/date.utils';

/**
 * Страница «Отчёт по прокату»: плоский текст со всеми полями rental-detail,
 * который удобно скопировать и переслать в мессенджер.
 */
@Component({
  selector: 'app-rental-report',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  templateUrl: './rental-report.component.html',
  styleUrl: './rental-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RentalReportComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly rentalService = inject(RentalService);
  private readonly paymentService = inject(PaymentService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly platformId = inject(PLATFORM_ID);

  readonly loading = signal(true);
  readonly rental = signal<Rental | null>(null);
  readonly payments = signal<Payment[]>([]);
  readonly paymentsLoading = signal(false);
  readonly reportText = signal('');
  readonly rentalId = signal<number>(0);
  readonly copyError = signal(false);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.loading.set(false);
      return;
    }
    this.rentalId.set(id);
    this.rentalService.get(id).subscribe({
      next: (r) => {
        this.rental.set(r);
        this.reportText.set(this.buildReport(r, this.payments()));
        this.loading.set(false);
        this.loadPayments(id);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  private loadPayments(rentalId: number): void {
    this.paymentsLoading.set(true);
    this.paymentService.list(rentalId).subscribe({
      next: (res) => {
        const items = res.results || [];
        this.payments.set(items);
        this.paymentsLoading.set(false);
        const r = this.rental();
        if (r) {
          this.reportText.set(this.buildReport(r, items));
        }
      },
      error: () => {
        this.paymentsLoading.set(false);
      },
    });
  }

  copy(): void {
    const text = this.reportText();
    if (!text) {
      this.snackBar.open('Отчёт ещё не сформирован', 'ОК', { duration: 3000 });
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      this.snackBar.open('Копирование недоступно в этой среде', 'ОК', { duration: 3000 });
      return;
    }
    this.copyToClipboard(text)
      .then(() => {
        this.copyError.set(false);
        this.snackBar.open('Скопировано', 'ОК', { duration: 2000 });
      })
      .catch(() => {
        this.copyError.set(true);
        this.snackBar.open('Ошибка копирования', 'ОК', { duration: 3000 });
      });
  }

  /**
   * Шеринг через системный Share Sheet (navigator.share).
   * На iPhone/в Telegram вставка многострочного текста уходит в Premium Rich Text,
   * поэтому для сохранения переносов строк лучше «Поделиться», а не копирование.
   */
  async share(): Promise<void> {
    const text = this.reportText();
    if (!text) {
      this.snackBar.open('Отчёт ещё не сформирован', 'ОК', { duration: 3000 });
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      this.snackBar.open('Поделиться недоступно в этой среде', 'ОК', { duration: 3000 });
      return;
    }
    if (typeof navigator.share !== 'function') {
      this.snackBar.open('Поделиться недоступно на этом устройстве', 'ОК', { duration: 3000 });
      return;
    }
    try {
      await navigator.share({ text });
    } catch (error) {
      // Отмена пользователем (AbortError, в т.ч. старый WebKit) — молча.
      if (this.isAbortError(error)) {
        return;
      }
      this.snackBar.open('Не удалось поделиться', 'ОК', { duration: 3000 });
    }
  }

  private isAbortError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return error.name === 'AbortError' || error.code === DOMException.ABORT_ERR;
    }
    const name = (error as { name?: unknown } | null)?.name;
    return name === 'AbortError' || name === 'NS_ERROR_DOM_ABORT_ERR';
  }

  /**
   * Сначала пробуем нативный Clipboard API (требует secure context),
   * в фоллбэке — скрытая `<textarea>` + `document.execCommand('copy')`,
   * который работает внутри Telegram Mini App и старых WebView.
   */
  private async copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Падаем в фоллбэк, если браузер отказал (например, нет focus).
      }
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
    if (!ok) {
      throw new Error('execCommand copy failed');
    }
  }

  private fmt(value: string | null | undefined): string {
    return value ? formatDateTime(value) : '—';
  }

  private num(value: number | null | undefined): string {
    return value === null || value === undefined ? '—' : String(value);
  }

  private paidTotal(payments: Payment[]): number {
    return payments.reduce((sum, p) => {
      if (!p.is_paid) return sum;
      const v = Number(p.amount);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }

  private extraServicesTotal(extraServices: RentalExtraService[] | undefined): number {
    return (extraServices ?? []).reduce((sum, es) => {
      const v = Number(es.amount);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }

  private buildReport(r: Rental, payments: Payment[]): string {
    const lines: string[] = [];

    lines.push(`Прокат #${r.id}`);
    lines.push(`Статус: ${r.status_display || '—'}`);
    lines.push(`Автомобиль: ${r.car_title || '—'}`);
    lines.push(`Госномер: ${r.license_plate || '—'}`);
    lines.push(`Клиент: ${r.client_name || '—'}`);

    if (r.handover_location) {
      lines.push(`Место выдачи: ${r.handover_location}`);
    }
    lines.push(`Дата/время выдачи: ${this.fmt(r.handover_datetime)}`);

    if (r.return_location) {
      lines.push(`Место возврата: ${r.return_location}`);
    }
    if (r.return_datetime) {
      lines.push(`Дата/время возврата: ${this.fmt(r.return_datetime)}`);
    }
    if (r.expected_return_datetime) {
      lines.push(`Ожидаемая дата/время возврата: ${this.fmt(r.expected_return_datetime)}`);
    }
    if (r.route) {
      lines.push(`Маршрут: ${r.route}`);
    }

    if (r.notes) {
      lines.push('');
      lines.push('Заметки:');
      lines.push(r.notes);
    }

    lines.push('');
    lines.push('Платежи:');
    if (payments.length === 0) {
      lines.push('—');
    } else {
      payments.forEach((p, idx) => {
        lines.push(
          `${idx + 1}. ${p.amount} ₽ — ${p.payment_method_display}`,
        );
      });
      const total = this.paidTotal(payments);
      if (total > 0) {
        lines.push(`Итого: ${total} ₽`);
      }
    }

    lines.push('');
    lines.push('Доп. услуги:');
    const extra = r.extra_services ?? [];
    if (extra.length === 0) {
      lines.push('—');
    } else {
      extra.forEach((es, idx) => {
        lines.push(`${idx + 1}. ${es.name} — ${es.amount} ₽`);
      });
      const total = this.extraServicesTotal(extra);
      if (total > 0) {
        lines.push(`Итого: ${total} ₽`);
      }
    }

    lines.push('');
    lines.push('Итоги проката:');
    lines.push(`Уровень топлива при выдаче: ${this.num(r.fuel_level_before)}`);
    lines.push(`Уровень топлива при возврате: ${this.num(r.fuel_level_after)}`);
    lines.push(`Расход топлива: ${this.num(r.fuel_consumed)}`);
    lines.push(`Пробег при выдаче: ${this.num(r.mileage_before)} км`);
    lines.push(`Пробег при возврате: ${this.num(r.mileage_after)} км`);
    lines.push(`Пройдено: ${this.num(r.distance)} км`);

    if (r.mileage_overrun !== null && r.mileage_overrun !== undefined) {
      const limitPart =
        r.car_mileage_limit !== null && r.car_mileage_limit !== undefined
          ? ` (лимит: ${r.car_mileage_limit} км/сутки)`
          : '';
      lines.push(`Перепробег: ${r.mileage_overrun} км${limitPart}`);
    } else {
      lines.push('Перепробег: —');
    }

    return lines.join('\n');
  }
}
